/**
 * PaymentActivityList — Phase 85.1
 *
 * Payment activity for one liability: date, total, the principal/interest/fee
 * split when it is actually known, match status, where the split came from,
 * and an unlink action. Never invents a split.
 */
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatMoney } from '@/lib/format';
import { ALLOCATION_SOURCE_LABEL, MATCH_STATUS_LABEL } from '@/lib/liabilityReconcileApi';

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

const STATUS_VARIANT = {
  user_confirmed: 'secondary',
  auto_linked: 'secondary',
  suggested: 'outline',
  rejected: 'outline',
  reversed: 'outline',
};

export function PaymentActivityList({
  events = [], currency = 'INR', onConfirm, onReject, onReverse, busy = false,
}) {
  if (!events.length) {
    return (
      <div
        className="bg-card rounded-xl border border-border p-5 shadow-sm"
        data-testid="liab-payment-activity-empty"
      >
        <p className="text-xs font-semibold text-charcoal mb-1">Payment activity</p>
        <p className="text-xs text-muted-foreground leading-relaxed">
          No payments linked to this loan yet. When you import a statement, 2120 looks
          for payments that match this loan and asks you to confirm them.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border p-5 shadow-sm" data-testid="liab-payment-activity">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-charcoal">Payment activity</p>
        <p className="text-[11px] text-muted-foreground">{events.length} payment{events.length === 1 ? '' : 's'}</p>
      </div>

      <div className="space-y-2.5">
        {events.map((e) => {
          const hasSplit = e.principal_amount != null || e.interest_amount != null;
          const dim = e.match_status === 'rejected' || e.match_status === 'reversed';
          return (
            <div
              key={e.id}
              className={`rounded-lg border border-border/70 px-3.5 py-3 ${dim ? 'opacity-55' : ''}`}
              data-testid={`liab-payment-event-${e.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="num text-sm font-medium text-charcoal">
                    {formatMoney(e.total_payment || 0, e.currency || currency)}
                    <span className="text-xs text-muted-foreground font-normal">
                      {' '}· {fmtDate(e.payment_date)}
                    </span>
                  </p>
                  {e.description && (
                    <p className="text-[11px] text-muted-foreground truncate mt-0.5">{e.description}</p>
                  )}
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {hasSplit ? (
                      <>
                        {e.principal_amount != null && `Principal ${formatMoney(e.principal_amount, e.currency || currency)}`}
                        {e.interest_amount != null && ` · Interest ${formatMoney(e.interest_amount, e.currency || currency)}`}
                        {e.fee_amount ? ` · Fees ${formatMoney(e.fee_amount, e.currency || currency)}` : ''}
                      </>
                    ) : (
                      'Principal and interest split not known yet'
                    )}
                  </p>
                  {/* Provenance of the split. Suppressed when unknown — the
                      line above already says so, and repeating it reads oddly. */}
                  {hasSplit && (
                    <p className="text-[10px] text-muted-foreground/80 mt-0.5">
                      {ALLOCATION_SOURCE_LABEL[e.allocation_source] || e.allocation_source}
                      {e.estimated_balance_after != null && (
                        <>
                          {' · '}
                          <span data-testid={`liab-payment-balance-after-${e.id}`}>
                            Balance after {formatMoney(e.estimated_balance_after, e.currency || currency)} (estimated)
                          </span>
                        </>
                      )}
                    </p>
                  )}
                </div>

                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  <Badge
                    variant={STATUS_VARIANT[e.match_status] || 'outline'}
                    className="text-[10px]"
                    data-testid={`liab-payment-status-${e.id}`}
                  >
                    {MATCH_STATUS_LABEL[e.match_status] || e.match_status}
                  </Badge>
                  <div className="flex gap-1">
                    {e.match_status === 'suggested' && (
                      <>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" disabled={busy}
                          onClick={() => onConfirm?.(e)} data-testid={`liab-payment-confirm-${e.id}`}>
                          Confirm
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" disabled={busy}
                          onClick={() => onReject?.(e)} data-testid={`liab-payment-reject-${e.id}`}>
                          Not this loan
                        </Button>
                      </>
                    )}
                    {(e.match_status === 'user_confirmed' || e.match_status === 'auto_linked') && (
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" disabled={busy}
                        onClick={() => onReverse?.(e)} data-testid={`liab-payment-unlink-${e.id}`}>
                        Unlink
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default PaymentActivityList;
